import {useEffect, useRef} from 'react';

import {isFunction, isNumber} from 'ramda-adjunct';

import {TestIdProps, useOnMount} from 'shared/web';

import {Alert} from '../../Alert/Alert';
import {useTranslationContext} from '../../TranslationProvider/TranslationContext';
import {NotificationProps} from '../Notification.types';
import {getAutoClose} from '../utils/getAutoClose';
import {getDefaultMessage} from '../utils/getDefaultMessage';

export interface NotificationContainerProps extends TestIdProps {
  notification: NotificationProps;
  onHide(id: string): void;
  autoClose: false | number;
}

export function NotificationContainer(props: NotificationContainerProps) {
  const {notification, autoClose, onHide} = props;
  const {variant} = notification;
  const t = useTranslationContext();
  const defaultMessage = getDefaultMessage(variant, t);

  const {autoClose: notificationAutoClose} = notification;
  const autoCloseTimeout = getAutoClose(autoClose, notificationAutoClose);
  const hideTimeout = useRef<number>(null);

  const handleHide = () => {
    onHide(notification.id as string);
    if (hideTimeout.current) {
      window.clearTimeout(hideTimeout.current);
    }
  };

  const cancelDelayedHide = () => {
    if (hideTimeout.current) {
      clearTimeout(hideTimeout.current);
    }
  };

  const handleDelayedHide = () => {
    if (isNumber(autoCloseTimeout)) {
      hideTimeout.current = window.setTimeout(handleHide, autoCloseTimeout);
    }
  };

  useOnMount(() => {
    if (isFunction(notification.onOpen)) {
      notification.onOpen(notification);
    }
  });

  useEffect(
    function scheduleAutoHide() {
      handleDelayedHide();
      return cancelDelayedHide;
    },
    [autoClose, notification.autoClose] // eslint-disable-line react/exhaustive-deps -- Muted pre-existing violation
  );

  const alertConfig =
    notification.layout === 'detailed'
      ? {
          layout: notification.layout,
          type: notification.type,
          variant: notification.variant,
          title: notification.title,
          message: notification.message || defaultMessage,
          hyperlinks: notification.hyperlinks,
          isLoading: notification.isLoading,
          actions: notification.actions,
          secondaryMessage: notification.secondaryMessage,
        }
      : {
          layout: notification.layout,
          type: notification.type,
          variant: notification.variant,
          title: notification.title,
          message: notification.message || defaultMessage,
          hyperlinks: notification.hyperlinks,
        };

  return (
    <div onMouseEnter={cancelDelayedHide} onMouseLeave={handleDelayedHide}>
      <Alert {...alertConfig} onClose={handleHide} />
    </div>
  );
}
